import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { CellLabel, cssLinearGradientFromStops, useLang } from '@shapeable/ui';
import { classNames } from '@shapeable/utils';
import { KEY_SERVICES_GRADIENT_STOPS } from '../../data';

const cls = classNames('key-services-legend');

const SCALE_TICKS = [0, 1, 2, 3, 4, 5];

export type KeyServicesLegendProps = Classable & HasChildren & {
  stops?: string[];
  maxValue?: number;
};

export const KeyServicesLegendDefaultProps: KeyServicesLegendProps = {
  stops: KEY_SERVICES_GRADIENT_STOPS,
  maxValue: 5,
};

type GradientBarProps = {
  _gradient: string;
};

const ContainerStyles = breakpoints({
  base: css`
    display: inline-flex;
    flex-direction: column;
    gap: ${theme.UNIT(1.5)};
    padding: ${theme.UNIT(2)} ${theme.UNIT(2.5)};
    background: ${theme.COLOR('light')};
    border-radius: 10px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    max-width: 100%;
    box-sizing: border-box;
  `,
  tablet: css`
    padding: ${theme.UNIT(2)} ${theme.UNIT(3)};
  `,
});

const TitleStyles = breakpoints({
  base: css`
    margin: 0;
    font-size: ${theme.FONT_SIZE(10)};
    font-weight: 600;
    letter-spacing: 0.02em;
    color: ${theme.COLOR('dark')};
    line-height: 1.2;
  `,
  tablet: css`
    font-size: ${theme.FONT_SIZE(11)};
  `,
});

const ScaleWrapStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(0.75)};
    min-width: 140px;
  `,
  tablet: css`
    min-width: 180px;
  `,
});

const GradientBarStyles = breakpoints({
  base: css`
    width: 100%;
    height: 12px;
    border-radius: 2px;
    background: ${({ _gradient }: GradientBarProps) => _gradient};
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  `,
  tablet: css`
    height: 14px;
  `,
});

const TicksStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0 1px;
  `,
});

const TickStyles = breakpoints({
  base: css`
    font-size: ${theme.FONT_SIZE(10)};
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${theme.COLOR('dark')};
    line-height: 1;
  `,
  tablet: css`
    font-size: ${theme.FONT_SIZE(11)};
  `,
});

const My = {
  Container: styled.div`${ContainerStyles}`,
  Title: styled(CellLabel)`${TitleStyles}`,
  ScaleWrap: styled.div`${ScaleWrapStyles}`,
  GradientBar: styled.div<GradientBarProps>`${GradientBarStyles}`,
  Ticks: styled.div`${TicksStyles}`,
  Tick: styled.span`${TickStyles}`,
};

export const KeyServicesLegend: Shapeable.FC<KeyServicesLegendProps> = (props) => {
  const { className, stops = KEY_SERVICES_GRADIENT_STOPS } = props;
  const t = useLang();
  const gradient = cssLinearGradientFromStops(stops);

  return (
    <My.Container className={cls.name(className)} role="img" aria-label={t('Mean score legend')}>
      <My.Title>{t('Mean score')}</My.Title>
      <My.ScaleWrap>
        <My.GradientBar _gradient={gradient} />
        <My.Ticks>
          {SCALE_TICKS.map((tick) => (
            <My.Tick key={tick}>{tick}</My.Tick>
          ))}
        </My.Ticks>
      </My.ScaleWrap>
    </My.Container>
  );
};

KeyServicesLegend.cls = cls;
KeyServicesLegend.defaultProps = KeyServicesLegendDefaultProps;
